import React, { useCallback, useEffect, useState } from 'react';
import FixedAssetForm from './FixedAssetForm';
import FixedAssetList from './FixedAssetList';
import DepreciationCalculator from './DepreciationCalculator';
import MonthlyProcessSection from './MonthlyProcessSection';
import AnnualUpdateSection from './AnnualUpdateSection';
import {
  FixedAsset,
  FixedAssetFormValues,
  FixedAssetSearchFilter,
  KamokuOption,
} from './fixedAssetTypes';
import { getErrorMessage } from '../utils/errorHandler';

interface FixedAssetManagementProps {
  onBack: () => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const PAGE_SIZE = 10;

const FixedAssetManagement: React.FC<FixedAssetManagementProps> = ({
  onBack,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [loadingAssets, setLoadingAssets] = useState<boolean>(false);
  const [kamokuOptions, setKamokuOptions] = useState<KamokuOption[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | null>(null);
  const [editingAsset, setEditingAsset] = useState<FixedAsset | null>(null);
  const [searchFilter, setSearchFilter] = useState<FixedAssetSearchFilter>({
    assetName: '',
    location: '',
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedAssetId = selectedAsset?.id ?? null;

  const fetchKamokuOptions = useCallback(async () => {
    if (currentTenantId == null) {
      setKamokuOptions([]);
      return;
    }
    try {
      const response = await apiFetch('/api/kamoku');
      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '勘定科目の取得に失敗しました');
        throw new Error(errorMessage);
      }
      const data = await response.json();
      const options: KamokuOption[] = (Array.isArray(data) ? data : []).map((item: any) => ({
        id: Number(item.id),
        name: item.kamoku ?? item.kamoku_name ?? item.name ?? `科目${item.id}`,
        code: item.kamoku_code ?? item.code ?? undefined,
      }));
      setKamokuOptions(options);
    } catch (err) {
      console.error(err);
      setKamokuOptions([]);
    }
  }, [apiFetch, currentTenantId]);

  const fetchAssets = useCallback(async () => {
    if (currentTenantId == null) {
      setAssets([]);
      setTotal(0);
      setSelectedAsset(null);
      setEditingAsset(null);
      setErrorMessage(null);
      return;
    }
    try {
      setLoadingAssets(true);
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
      });
      if (searchFilter.assetName.trim()) {
        params.append('asset_name', searchFilter.assetName.trim());
      }
      if (searchFilter.location.trim()) {
        params.append('location', searchFilter.location.trim());
      }
      const response = await apiFetch(`/api/fixed-assets?${params.toString()}`);
      if (!response.ok) {
        const message = await getErrorMessage(response, '固定資産の取得に失敗しました。');
        throw new Error(message);
      }
      const data = await response.json();
      const items: FixedAsset[] = data.items ?? [];
      setAssets(items);
      setTotal(data.total ?? items.length ?? 0);
      setErrorMessage(null);
      if (items.length === 0) {
        setSelectedAsset(null);
      } else if (selectedAssetId != null) {
        const match = items.find((item) => item.id === selectedAssetId);
        if (match) {
          setSelectedAsset(match);
        } else {
          setSelectedAsset(items[0]);
        }
      } else {
        setSelectedAsset(items[0]);
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.message || '固定資産一覧の取得に失敗しました。');
    } finally {
      setLoadingAssets(false);
    }
  }, [apiFetch, currentTenantId, page, searchFilter.assetName, searchFilter.location, selectedAssetId]);

  useEffect(() => {
    fetchKamokuOptions();
  }, [fetchKamokuOptions]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleSearchChange = (value: FixedAssetSearchFilter) => {
    setSearchFilter(value);
    setPage(1);
    setStatusMessage(null);
    setErrorMessage(null);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    setStatusMessage(null);
    setErrorMessage(null);
  };

  const buildPayload = (values: FixedAssetFormValues) => {
    return {
      asset_name: values.asset_name.trim(),
      acquisition_date: values.acquisition_date,
      acquisition_cost: Number(values.acquisition_cost),
      useful_life_years: Number(values.useful_life_years),
      location: values.location.trim() || null,
      depreciation_method: '定額法',
      service_start_date: values.service_start_date,
      kamoku_id: values.kamoku_id ? Number(values.kamoku_id) : null,
      remarks: values.remarks.trim() || null,
    };
  };

  const handleSubmit = async (values: FixedAssetFormValues, assetId?: number | null) => {
    if (currentTenantId == null) {
      throw new Error('テナントが選択されていません。');
    }
    const payload = buildPayload(values);
    const isEdit = assetId != null;
    const url = isEdit ? `/api/fixed-assets/${assetId}` : '/api/fixed-assets';
    const method = isEdit ? 'PUT' : 'POST';

    const response = await apiFetch(url, {
      method,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const message = await getErrorMessage(
        response,
        `固定資産の${isEdit ? '更新' : '登録'}に失敗しました。`,
      );
      throw new Error(message);
    }
    const data = await response.json();
    setStatusMessage(isEdit ? '固定資産を更新しました。' : '固定資産を登録しました。');
    setErrorMessage(null);

    if (isEdit) {
      setEditingAsset(null);
      setSelectedAsset(data);
    } else {
      setPage(1);
      setSelectedAsset(data);
    }
    await fetchAssets();
  };

  const handleEdit = (asset: FixedAsset) => {
    setEditingAsset(asset);
    setSelectedAsset(asset);
    setStatusMessage(null);
    setErrorMessage(null);
  };

  const handleCancelEdit = () => {
    setEditingAsset(null);
    setStatusMessage(null);
    setErrorMessage(null);
  };

  const handleDelete = async (asset: FixedAsset) => {
    try {
      const response = await apiFetch(`/api/fixed-assets/${asset.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, '固定資産の削除に失敗しました。');
        throw new Error(message);
      }
      setStatusMessage('固定資産を削除しました。');
      setErrorMessage(null);
      if (selectedAsset?.id === asset.id) {
        setSelectedAsset(null);
      }
      if (assets.length <= 1 && page > 1) {
        setPage(page - 1);
      } else {
        await fetchAssets();
      }
    } catch (err: any) {
      setErrorMessage(err?.message || '固定資産の削除に失敗しました。');
    }
  };

  const bodyContent = () => {
    if (currentTenantId == null) {
      return (
        <div style={{ padding: '40px', fontSize: '12px', color: '#333' }}>
          固定資産管理機能を利用するには、テナントを選択してください。
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: '12px', height: 'calc(100vh - 140px)' }}>
        <FixedAssetForm
          mode={editingAsset ? 'edit' : 'create'}
          asset={editingAsset}
          kamokuOptions={kamokuOptions}
          onSubmit={handleSubmit}
          onCancelEdit={handleCancelEdit}
          loading={loadingAssets}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FixedAssetList
            assets={assets}
            total={total}
            page={page}
            pageSize={PAGE_SIZE}
            loading={loadingAssets}
            search={searchFilter}
            onSearchChange={handleSearchChange}
            onPageChange={handlePageChange}
            onReload={fetchAssets}
            onSelect={(assetItem) => setSelectedAsset(assetItem)}
            selectedAssetId={selectedAsset?.id ?? null}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCalculate={(assetItem) => setSelectedAsset(assetItem)}
            apiFetch={apiFetch}
          />
          <DepreciationCalculator asset={selectedAsset} apiFetch={apiFetch} />
          <MonthlyProcessSection apiFetch={apiFetch} />
          <AnnualUpdateSection apiFetch={apiFetch} />
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#003366', marginBottom: '4px' }}>
          テナント: {tenantName}
        </div>
        {statusMessage && (
          <div
            style={{
              backgroundColor: '#e8f5e9',
              border: '1px solid #2e7d32',
              color: '#2e7d32',
              padding: '6px',
              fontSize: '11px',
              marginBottom: '6px',
            }}
          >
            {statusMessage}
          </div>
        )}
        {errorMessage && (
          <div
            style={{
              backgroundColor: '#ffebee',
              border: '1px solid #c62828',
              color: '#c62828',
              padding: '6px',
              fontSize: '11px',
              marginBottom: '6px',
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      {bodyContent()}
    </div>
  );
};

export default FixedAssetManagement;
